/**
 * DatabaseWriteService.js
 * 
 * Service for writing database data to Firestore.
 */

// Firebase
import { deleteDoc, doc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { generateKey } from 'utilities/Keys';

// Constants
const DATABASES_COLLECTION = collections.collections;

/**
 * Creates a new database in Firestore.
 * 
 * @param {{ key?: string, database?: Object }} options - Options containing an optional key and the database data.
 * @param {string} [options.key] - Optional key for the database. If not provided, a new key will be generated.
 * @param {string} options.workspaceKey - The workspace key to which the database belongs.
 * @param {string} [options.userKey] - The user key associated with the database.
 * @param {Object} [options.database] - The database data to be created.
 * 
 * @returns {Promise<Object|null>} The created database data or null if database data is not provided.
 */
export async function createDatabase({ key, workspaceKey, userKey, database } = {}) {

  const databaseData = database;

  // Validate database data
  if (!databaseData) return null;

  // Validate workspaceKey
  if (!workspaceKey) return null;

  // Validate userKey
  if (!userKey) return null;

  // Determine database key
  const databaseKey = key || databaseData?.key || generateKey();

  // Timestamps
  const now = Timestamp.now();

  // Prepare payload
  const payload = {
    ...databaseData,
    key: databaseKey,
    workspaceKey,
    userKey,
    dateCreated: databaseData?.dateCreated || now,
    dateModified: databaseData?.dateModified || now,
  };

  // Create database document
  await setDoc(doc(db, DATABASES_COLLECTION, databaseKey), payload, { merge: false });

  // Return the created database payload
  return payload;
}

/**
 * Updates an existing database in Firestore.
 * 
 * @param {string} databaseKey - The key of the database to update.
 * @param {Object} data - The data to update the database with.
 * @param {string} userKey - The user key associated with the database.
 * 
 * @returns {Promise<void>}
 */
export async function updateDatabase(databaseKey, data, userKey) {

  // Validate inputs
  if (!databaseKey || !data || !userKey) return;

  // Prevent userKey changes
  if (data?.userKey && data.userKey !== userKey) return;

  // Prepare payload with updated dateModified
  const payload = {
    ...data,
    userKey,
    dateModified: data?.dateModified || Timestamp.now(),
  };

  // Update database document
  await updateDoc(doc(db, DATABASES_COLLECTION, databaseKey), payload);
}

/**
 * Deletes a database by its key.
 * 
 * @param {string} databaseKey - The key of the database to delete.
 * @param {string} userKey - The user key associated with the database.
 * 
 * @returns {Promise<void>} - Promise that resolves when the database is deleted.
 */
export async function deleteDatabaseByKey(databaseKey, userKey) {

  // Validate input
  if (!databaseKey || !userKey) return;

  // Delete database document
  await deleteDoc(doc(db, DATABASES_COLLECTION, databaseKey));
}
